import { useMemo } from "react";
import { useSelector } from "react-redux";
import { EventsState } from "../../../store/types";
import { CallToAction, CallToActionType, LanguagesAbbr, ModuleMap, PageLinkType, PageModuleType } from "../../../types/working-model";
import { updateTranslateKey } from "../../../utils/utils";
import Icon, { COLORS, ICONS } from "../icon";
import SelectInput from "../select/select";
import TextInput from "../text-input/text";
import './call-to-action.scss';

//if more page links are needed add them here and to the PageLinkType in working-model.ts
const pageOptions = [
	{
		label: "Registration",
		value: PageLinkType.registration
	},
	{
		label: "Landing Page",
		value: PageLinkType.landing
	},
	{
		label: "Terms & Conditions",
		value: PageLinkType.termsAndConditions
	},
	{
		label: "Privacy Policy",
		value: PageLinkType.privacyPolicy
	},
	{
		label: "Home Page",
		value: PageLinkType.homepage
	}
];

interface CallToActionProps {
	workingCTA?: CallToAction;
	onWorkingCTAChange: (workingCallToAction: CallToAction) => void;
	useFocusClick?: boolean;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr
}

const initRegistrationCTA: CallToAction = {
	type: CallToActionType.page,
	button_name: {
		base: "Register now",
		changed: ""
	},
	content: { page: PageLinkType.registration },
};

const CallToActionForm: React.FC<CallToActionProps> = ({ workingCTA, onWorkingCTAChange, useFocusClick, language, baseLanguage }) => {
	if (!workingCTA) {
		workingCTA = initRegistrationCTA;
	}

	const {
		CreateEventReducer: {
			workingEvent
		},
	} = useSelector((event: EventsState) => event);

	function handleTypeChange(type: CallToActionType) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				type
			});
		}
	}
	function handleNameChange({ target }: React.ChangeEvent<HTMLInputElement>) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				button_name: updateTranslateKey({
					translateString: workingCTA?.button_name,
					input: target.value,
					language,
					baseLanguage
				})
			});
		}
	}
	function handleSectionDest(value: PageModuleType) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				content: { section: value }
			});
		}
	}
	function handlePageDest(value: PageLinkType) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				content: { page: value }
			});
		}
	}
	function handleLinkDest(e: React.ChangeEvent<HTMLInputElement>) {
		if (workingCTA) {
			onWorkingCTAChange({
				...workingCTA,
				content: { url: e.target.value }
			});
		}
	}

	//module options
	const sectionOptions = useMemo(() => {
		return workingEvent?.homepage?.modules
			.filter(module => module.is_on)
			.map(module => ({
				value: module.type,
				label: ModuleMap[module.type]
			}));
	}, [workingEvent]);

	return (
		<div className="call-to-action-settings">
			<div className="call-to-action-upper">
				<TextInput
					id="button-text"
					label="Button text"
					placeholder={"Enter button text"}
					defaultValue={workingCTA?.button_name?.[language] as string || workingCTA?.button_name?.base || ''}
					onChange={handleNameChange}
					useFocusClick={useFocusClick}
				/>
				<SelectInput
					mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} />}
					options={[
						{
							label: "Section",
							value: CallToActionType.section
						},
						{
							label: "Page",
							value: CallToActionType.page
						},
						{
							label: "URL",
							value: CallToActionType.url
						}
					]}
					selected={workingCTA.type}
					onChange={handleTypeChange}
					label={"Link to *"}
				/>

			</div>
			{workingCTA.type === CallToActionType.section && (
				<div className="call-to-action-lower">
					<SelectInput
						id="select-section"
						mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} />}
						options={sectionOptions || []}
						// check if the section is main_event_banner which has enum value of 0
						selected={workingCTA.content?.section === 0 ? PageModuleType.main_event_banner : workingCTA.content?.section || ""}
						onChange={handleSectionDest}
						placeholder={"Select section"}
						label={"Section"}
					/>
				</div>
			)}
			{workingCTA.type === CallToActionType.page && (
				<div className="call-to-action-lower">
					<SelectInput
						id="select-page"
						mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} />}
						options={pageOptions}
						selected={workingCTA.content?.page || ""}
						onChange={handlePageDest}
						placeholder={"Select page"}
						label={"Page"}
					/>
				</div>
			)}

			{workingCTA.type === CallToActionType.url && (
				<div className="call-to-action-lower">
					<TextInput
						id="enter-url"
						label="URL"
						placeholder={"Enter URL"}
						onChange={handleLinkDest}
						defaultValue={workingCTA.content?.url}
						useFocusClick={useFocusClick}
					/>
				</div>
			)}
		</div>
	);
};

export default CallToActionForm;
